/*
 * The admin Configuration editor's code-side registry view (ADR-0005, database seed). Keys that
 * appear here are the only ones the UI can render or write — there is no free-form key creation.
 * Classification (public / private) is structural; secrets never enter the store and are omitted
 * from this list entirely (env-only: feed key, JWT secret, webhook bearer).
 */

export type ConfigClassification = 'public' | 'private';

/** How the editor renders a key's value (typed controls, not a generic JSON blob for every key). */
export type ConfigControlKind =
  'toggle' | 'number' | 'text' | 'string-list' | 'json' | 'nullable-text';

export interface ConfigRegistryEntry {
  readonly key: string;
  readonly label: string;
  readonly classification: ConfigClassification;
  readonly control: ConfigControlKind;
  /** Short operator-facing description. */
  readonly description: string;
}

/**
 * Registry order matches the documented seed (private first, then public). Labels are operator-
 * facing; keys are the API-10 identifiers.
 */
export const CONFIG_REGISTRY: readonly ConfigRegistryEntry[] = [
  {
    key: 'default_configuration',
    label: 'Default configuration',
    classification: 'private',
    control: 'json',
    description:
      'Pick min/max, lock/void/push policies, and the correction window Contests snapshot.',
  },
  {
    key: 'award_retry_cap',
    label: 'Award retry cap',
    classification: 'private',
    control: 'number',
    description: 'Maximum delivery attempts before an Award is Failed.',
  },
  {
    key: 'segment_registry',
    label: 'Segment registry',
    classification: 'private',
    control: 'json',
    description: 'Opaque Caesars segment id → admin label (hot-reload).',
  },
  {
    key: 'sport_allowlist',
    label: 'Sport allowlist',
    classification: 'private',
    control: 'string-list',
    description: 'Sports admitted into the Live catalogue.',
  },
  {
    key: 'competition_allowlist',
    label: 'Competition allowlist',
    classification: 'private',
    control: 'string-list',
    description: 'Competitions admitted into the Live catalogue.',
  },
  {
    key: 'market_type_allowlist',
    label: 'Market type allowlist',
    classification: 'private',
    control: 'string-list',
    description: 'Curated Over/Under market types.',
  },
  {
    key: 'odds_margin',
    label: 'Odds margin',
    classification: 'private',
    control: 'json',
    description: 'Global and per-market-type decimal-odds bounds.',
  },
  {
    key: 'session_ttl',
    label: 'Session TTL',
    classification: 'private',
    control: 'number',
    description: 'Player session lifetime in seconds (fixed; no sliding refresh).',
  },
  {
    key: 'alert_thresholds',
    label: 'Alert thresholds',
    classification: 'private',
    control: 'json',
    description: 'Reconciliation-sweep age thresholds.',
  },
  {
    key: 'maintenance_mode',
    label: 'Maintenance mode',
    classification: 'public',
    control: 'toggle',
    description: 'Public flag served on the read-path blob.',
  },
  {
    key: 'launch_gate',
    label: 'Launch gate',
    classification: 'public',
    control: 'nullable-text',
    description: 'Public launch gate message, or empty when open.',
  },
  {
    key: 'display_copy',
    label: 'Public display copy',
    classification: 'public',
    control: 'json',
    description: 'Player-facing copy for no-contest, locked, and unavailable states.',
  },
] as const;

export const CONFIG_REGISTRY_KEYS: readonly string[] = CONFIG_REGISTRY.map((entry) => entry.key);

/** Look up a registry entry by key, or null when the key is outside the registry. */
export function registryEntry(key: string): ConfigRegistryEntry | null {
  return CONFIG_REGISTRY.find((entry) => entry.key === key) ?? null;
}
